import { client } from './api';

/**
 * Which agencies have a fax number, and getting one for an agency that has none.
 *
 * An agency can hold several. A multi-site agency has one per branch that
 * referrers already know, and an agency porting in may bring three, so this is
 * a list per agency rather than a has-one flag.
 *
 * Every call passes `throwOnError` because the generated client resolves rather
 * than rejects on a 4xx by default, which would show a success toast over a 403.
 */

export interface AgencyFaxNumber {
  id: string;
  ringcentral_extension_id: string;
  /** `ringcentral` routes by extension; `telnyx` routes by the dialled number. */
  provider: string;
  routing_key: string;
  routing_key_type: string;
  fax_number: string | null;
  label: string | null;
  is_active: boolean;
  agency_clinic_id: string | null;
  clinic_name: string | null;
}

export interface AgencyFaxNumbers {
  agency_id: string;
  agency_name: string;
  numbers: AgencyFaxNumber[];
}

export interface ProvisionFaxNumberBody {
  agency_id: string;
  /** Three digits. A branch wants a number local to its own area. */
  area_code: string;
  /** Left unset, the agency's whole staff sees faxes to this number. */
  agency_clinic_id?: string | null;
  label?: string | null;
}

export async function listAgencyFaxNumbers(): Promise<AgencyFaxNumbers[]> {
  const response = await client.get<AgencyFaxNumbers[], unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: '/internal-tools/fax-numbers',
  });
  return response.data;
}

/**
 * Buy a number and route it to the agency.
 *
 * Refused with a 403 unless the deployment is allowed to purchase, which is off
 * everywhere by default: a number is a recurring charge, so somebody has to
 * turn it on deliberately.
 */
export async function provisionFaxNumber(
  body: ProvisionFaxNumberBody
): Promise<AgencyFaxNumber> {
  const response = await client.post<AgencyFaxNumber, unknown, true>({
    body,
    responseType: 'json',
    throwOnError: true,
    url: '/internal-tools/fax-numbers/provision',
  });
  return response.data;
}

/**
 * Numbers claimed by more than one mapping, across every agency.
 *
 * The column is descriptive and deliberately not unique, so nothing in the
 * database stops an operator typing the same number against two extensions.
 * The screen is the only place that would ever notice, and the consequence is
 * that somebody reads the wrong agency's fax line as theirs.
 */
export function duplicateFaxNumbers(agencies: AgencyFaxNumbers[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const agency of agencies) {
    for (const number of agency.numbers) {
      if (!number.fax_number) continue;
      if (seen.has(number.fax_number)) duplicates.add(number.fax_number);
      seen.add(number.fax_number);
    }
  }
  return duplicates;
}

/**
 * One extension on the RingCentral account, and whether an agency has it.
 *
 * Our application may read the account but not write to it, so an extension
 * exists here only once somebody has created it in the RingCentral console.
 */
export interface RingCentralExtension {
  extension_id: string;
  name: string | null;
  extension_number: string | null;
  status: string | null;
  /** FaxUser, User, CompanyExtension. Shown so the choice is informed. */
  type: string | null;
  /** An extension can hold more than one number. */
  fax_numbers: string[];
  claimed_by_agency_id: string | null;
  claimed_by_agency_name: string | null;
  /** Faxes already parked on this extension, waiting for a mapping. */
  waiting_fax_count: number;
  /**
   * The subset of fax_numbers RingCentral has not settled yet, which in
   * practice means a port from another carrier is still running. The number
   * shown during that window is temporary and changes at cutover, so it is
   * not worth recording against an agency. Routing is unaffected: a fax
   * carries the extension, not the number it was dialled on.
   */
  porting_numbers: string[];
}

export interface RingCentralInventory {
  extensions: RingCentralExtension[];
  /** False everywhere today, pending a RingCentral permission approval. */
  can_purchase: boolean;
}

export interface LinkFaxNumberBody {
  ringcentral_extension_id: string;
  agency_id: string;
  agency_clinic_id?: string | null;
  fax_number?: string | null;
  label?: string | null;
  /**
   * Also hand this agency whatever is already parked on the extension.
   *
   * Off unless asked for: placing a fax cannot be undone, so it is not
   * something to do by default on the strength of a mis-picked agency.
   */
  claim_waiting_faxes?: boolean;
}

/** Two RingCentral reads, so call it when the picker opens, not on page load. */
export async function listRingCentralInventory(): Promise<RingCentralInventory> {
  const response = await client.get<RingCentralInventory, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: '/internal-tools/fax-numbers/ringcentral',
  });
  return response.data;
}

export interface LinkedFaxNumber extends AgencyFaxNumber {
  /** How many parked faxes this call collected. Zero when none were asked for. */
  claimed_fax_count: number;
}

/**
 * Route an extension that already exists to an agency.
 *
 * This is the whole provisioning story while account writes are refused: a
 * person creates the extension and buys the number in the RingCentral console,
 * and this records who its faxes belong to. A 409 means the extension already
 * has an active mapping.
 */
export async function linkFaxNumber(
  body: LinkFaxNumberBody
): Promise<LinkedFaxNumber> {
  const response = await client.post<LinkedFaxNumber, unknown, true>({
    body,
    responseType: 'json',
    throwOnError: true,
    url: '/internal-tools/fax-number-mappings',
  });
  return response.data;
}

/**
 * Take a number out of service, or put it back.
 *
 * Deactivating rather than deleting, because the row is what recorded who an
 * extension's faxes belonged to and for how long. The lookup considers active
 * rows only, so a deactivated mapping stops routing immediately, and both
 * uniqueness indexes constrain active rows only, so the extension and the
 * number are free for another mapping straight away.
 *
 * Reactivating can therefore fail with a 409 where deactivating cannot:
 * something else may have taken the extension or the number in between.
 */
export async function setFaxNumberMappingActive(
  mappingId: string,
  isActive: boolean
): Promise<AgencyFaxNumber> {
  const response = await client.patch<AgencyFaxNumber, unknown, true>({
    body: { is_active: isActive },
    responseType: 'json',
    throwOnError: true,
    url: `/internal-tools/fax-number-mappings/${mappingId}`,
  });
  return response.data;
}

/** Extensions nobody has claimed yet. */
export function unclaimedExtensions(
  inventory: RingCentralInventory | null
): RingCentralExtension[] {
  return (inventory?.extensions ?? []).filter(
    extension => extension.claimed_by_agency_id === null
  );
}

/**
 * The extensions actually worth offering: unclaimed, and holding a number.
 *
 * An extension with no number cannot receive a fax, so claiming one would look
 * like success and route nothing. The two cases are kept apart because the
 * screen tells them apart: all claimed and none numbered need different
 * instructions.
 */
export function claimableExtensions(
  inventory: RingCentralInventory | null
): RingCentralExtension[] {
  return unclaimedExtensions(inventory).filter(
    extension => extension.fax_numbers.length > 0
  );
}

/** How an extension reads in the picker: the number first, since that is what
 * an operator was given, then whatever names it. */
export function describeExtension(extension: RingCentralExtension): string {
  const number = extension.fax_numbers[0] ?? 'no number yet';
  const name = extension.name ?? `extension ${extension.extension_id}`;
  const kind = extension.type ? `, ${extension.type}` : '';
  return `${number} (${name}${kind})`;
}
